import { useEffect, useRef, useState } from 'react';
import { BrandMark, Icon } from '../common';
import { dayName, dayAndMonth } from './week';
import { parseLocal } from '../../lib/dates';
import { api, ApiError, logout } from '../../lib/api';
import { useFetchTrigger } from '../../lib/useFetchTrigger';
import { recordPageView } from '../../lib/activity';
import { TodayPanel, type BriefWhen, type TodayLoad } from './TodayPanel';
import { shiftsPanelWeeks, monthDividerBefore, dublinDateOf, weekdayLong } from './todayFormat';
import { makeTabDwell, dwellTabFor, type StaffTab } from './tabDwell';
import { StaffBottomBar, type StaffViewTab } from './StaffBottomBar';
import { InfoPanel, useShiftAlerts } from './InfoPanel';
import { registerWorker } from './pushClient';
import { businessDayPlus } from '@bbbk/schema';
import type { MeToday, SelfView, SelfViewShift, SelfViewWeek } from '@bbbk/schema';

// The date each look-ahead briefing should carry at instant `t` — one and two
// business days after Dublin's today, stepped through the schema's ONE
// day-step derivation (businessDayPlus) so client staleness checks and the
// server's payload dates cannot drift. Module-level and stable, so the
// staleness interval that closes over them (mounted once) never goes stale
// itself.
const tomorrowExpected = (t: number): string => businessDayPlus(dublinDateOf(t), 1);
const dayAfterExpected = (t: number): string => businessDayPlus(dublinDateOf(t), 2);

// The staff self-view (staff-self-view + staff-food-at-house +
// staff-daily-lookahead). A `staff`-role user lands here INSTEAD of the admin
// shell (App.tsx branches before BrowserRouter) — the rest of the app never
// mounts for them. Two tabs: Daily (the briefing — food at the house,
// medicine — with a day-pill selector: Today · Tomorrow · the day after,
// named by its date) and Shifts (the rota from the current week to the end of
// next month). Working today unlocks the two look-ahead pills; off today,
// only Today renders (the server enforces the same gate on the data). The
// same StaffViewBody is reused by the admin preview overlay, so an admin sees
// the same view a staff member does.

// The wire shapes are single-sourced in @bbbk/schema; re-exported so this
// module's importers keep the names they had when the types lived here.
export type SelfViewData = SelfView;
export type { SelfViewShift, SelfViewWeek };

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function rangeLabel(from: string, to: string): string {
  const a = parseLocal(from);
  const b = parseLocal(to);
  const aMonth = a.toLocaleDateString('en-IE', { month: 'long' });
  const bMonth = b.toLocaleDateString('en-IE', { month: 'long' });
  if (from === to) return `${a.getDate()} ${aMonth}`;
  if (aMonth === bMonth) return `${a.getDate()} – ${b.getDate()} ${aMonth}`;
  return `${a.getDate()} ${aMonth} – ${b.getDate()} ${bMonth}`;
}

function ShiftBadges({ shifts }: { shifts: SelfViewShift[] }) {
  const kinds = new Set(shifts.map((s) => s.kind));
  const showAm = kinds.has('am');
  const showPm = kinds.has('pm');
  return (
    <div className="shift-badges" aria-label={showAm && showPm ? 'AM + PM' : showAm ? 'AM' : 'PM'}>
      {showAm && <span className="shift-badge am">AM</span>}
      {showAm && showPm && <span className="shift-plus">+</span>}
      {showPm && <span className="shift-badge pm">PM</span>}
    </div>
  );
}

// One rota week card. The first two panel weeks read relatively ("This week",
// "Next week") with the muted date range beside; later weeks lead with dates.
function WeekCard({ week, title, range, changed }: { week: SelfViewWeek; title: string; range?: string; changed?: Set<string> }) {
  const byDate = new Map<string, SelfViewShift[]>();
  for (const s of week.shifts) {
    const list = byDate.get(s.date) ?? [];
    list.push(s);
    byDate.set(s.date, list);
  }
  // Opened from an alert (shift-alerts-send AC11): a changed day the helper no
  // longer holds anything on still gets a row this once, reading Off, so she
  // sees it go; next open it is simply not there.
  for (const date of changed ?? []) {
    if (date >= week.from && date <= week.to && !byDate.has(date)) byDate.set(date, []);
  }
  const days = [...byDate.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

  return (
    <section className={'staff-week' + (week.current ? ' current' : '')}>
      <header className="staff-week-head">
        <span>
          <span className="staff-week-title">{title}</span>
          {range && <span className="staff-week-range">{range}</span>}
        </span>
      </header>

      {days.length === 0 ? (
        <div className="staff-week-empty">No shifts this week</div>
      ) : (
        <ul className="staff-day-list">
          {days.map(([date, shifts]) => (
            <li key={date} className={'staff-day' + (changed?.has(date) ? ' changed' : '')}>
              <div className="staff-day-date">
                <span className="staff-day-wd">{dayName(date)}</span>
                <span className="staff-day-dm">{dayAndMonth(date)}</span>
              </div>
              {changed?.has(date) && <span className="staff-day-chg">Changed</span>}
              <div className="staff-shift-cell">
                {shifts.length > 0 ? <ShiftBadges shifts={shifts} /> : <span className="shift-badge off">Off</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// The Shifts spine: current week → end of next month (no previous weeks —
// Freddi's cut), month divider where a rendered week starts a new month.
export function ShiftsPanel({ data, changed }: { data: SelfViewData; changed?: string[] }) {
  const weeks = shiftsPanelWeeks(data.weeks);
  const changedSet = changed && changed.length > 0 ? new Set(changed) : undefined;
  return (
    <div className="staff-panel">
      {changedSet && (
        <div className="staff-changed-bar">
          <Icon name="bell" size={14} />
          <span>Changed just now · {changedSet.size} day{changedSet.size === 1 ? '' : 's'}</span>
        </div>
      )}
      {weeks.map((week, i) => {
        const divider = monthDividerBefore(weeks[i - 1], week);
        const title = i === 0 ? 'This week' : i === 1 ? 'Next week' : rangeLabel(week.from, week.to);
        const range = i <= 1 ? rangeLabel(week.from, week.to) : undefined;
        return (
          <div key={week.from} style={{ display: 'contents' }}>
            {divider && <div className="staff-month-divider" role="separator">{divider}</div>}
            <WeekCard week={week} title={title} range={range} changed={changedSet} />
          </div>
        );
      })}
    </div>
  );
}

// The shared, presentational body — greeting + Daily | Shifts tabs, with the
// day-pill selector inside Daily (staff-daily-lookahead). The staff page and
// the admin preview both render it, so the view cannot drift apart.
export function StaffViewBody({ data, todayLoad, tomorrowLoad, dayAfterLoad, onTabChange, email, preview, changed, initialTab }: { data: SelfViewData; todayLoad: TodayLoad; tomorrowLoad: TodayLoad; dayAfterLoad: TodayLoad; onTabChange?: (tab: StaffTab) => void; email?: string | null; preview?: { staffId: string }; changed?: string[]; initialTab?: StaffViewTab }) {
  // Opened from an alert, the page lands on Shifts with the changed days marked.
  const [tab, setTab] = useState<StaffViewTab>(initialTab ?? (changed && changed.length > 0 ? 'shifts' : 'daily'));
  // Shift alerts (shift-alerts-info-tab): the Info card's state, and the dot on
  // the Info tab while they are off. The preview reads the helper's server state.
  const alerts = useShiftAlerts(preview);
  const [day, setDay] = useState<BriefWhen>('today');
  // Look-ahead is EARNED by a shift today: pills for tomorrow and the day
  // after render only once the Today briefing confirms rostered (the server
  // enforces the same gate on the data — the UI just doesn't show doors the
  // caller can't open). Until confirmed (loading/error/off), Today alone.
  const lookAhead = todayLoad.state === 'ready' && todayLoad.data.rostered;
  // If the pill set collapses under the selected day (overnight turnover, a
  // refetch landing not-rostered), fall back to Today rather than a hidden day.
  const activeDay: BriefWhen = lookAhead ? day : 'today';

  // The dwell label the admin feed should be recording RIGHT NOW: the pill day
  // on the Daily tab (staff-today / staff-tomorrow / staff-day-after), else
  // staff-shifts. Derived from what is actually RENDERED (activeDay), not from
  // the last click.
  const dwellTab = dwellTabFor(tab, activeDay);

  // ONE reporting path, effect-driven — because the rendered day can change
  // WITHOUT a click: a silent revalidation landing `rostered: false` (the
  // per-minute day-boundary check) collapses the look-ahead and clamps
  // activeDay back to Today. Reporting only from the click handlers left the
  // tracker attributing that window to the stale look-ahead label. The ref
  // starts at the mounted label so the initial render reports nothing — the
  // tracker is already armed on 'today'.
  const reportedTab = useRef<StaffTab>(dwellTab);
  useEffect(() => {
    if (reportedTab.current === dwellTab) return;
    reportedTab.current = dwellTab;
    onTabChange?.(dwellTab);
  }, [dwellTab]);

  // Each day's pill carries the number of dogs LEAVING that day (Freddi,
  // 2026-07-24 — collections are the number that matters at a glance; this
  // replaced the earlier food-job count). Shown whenever the day is visible
  // (rostered = the gate passed), hidden at zero. The Daily tab is ALWAYS
  // today's leaving count — pinned to today whatever pill or tab is active,
  // so today's collections stay visible from the Shifts tab too.
  const leavingCount = (l: TodayLoad) =>
    l.state === 'ready' && l.data.rostered ? l.data.leavingDogs.length : 0;
  const todayCount = leavingCount(todayLoad);
  const tomorrowCount = leavingCount(tomorrowLoad);
  const dayAfterCount = leavingCount(dayAfterLoad);

  // The day-after pill is named by its WEEKDAY — "Sunday" (Freddi, 2026-07-24):
  // two days out, the day name is what a staff member actually thinks in; the
  // full date is a lookup. The panel's hero still carries the date in full.
  // The payload's date is the truth once loaded; until then, the client's own
  // Dublin calendar fills in. Lazy: only computed when the pill renders.
  const dayAfterIso = () => (dayAfterLoad.state === 'ready' ? dayAfterLoad.data.date : dayAfterExpected(Date.now()));

  const dayLoads: Record<BriefWhen, TodayLoad> = { today: todayLoad, tomorrow: tomorrowLoad, 'day-after': dayAfterLoad };

  const pill = (d: BriefWhen, label: string, count: number) => (
    <button
      key={d}
      className="staff-pill"
      type="button"
      aria-pressed={activeDay === d}
      onClick={() => setDay(d)}
    >
      {label} {count > 0 && <span className="count">{count}</span>}
    </button>
  );

  return (
    <div className="staff-body">
      <div className="staff-greeting">
        <h1>Hi {firstName(data.name)}</h1>
      </div>

      {tab === 'daily' && (
        <>
          <div className="staff-pills" role="group" aria-label="Briefing day">
            {pill('today', 'Today', todayCount)}
            {lookAhead && pill('tomorrow', 'Tomorrow', tomorrowCount)}
            {lookAhead && pill('day-after', weekdayLong(dayAfterIso()), dayAfterCount)}
          </div>
          <TodayPanel load={dayLoads[activeDay]} weeks={data.weeks} when={activeDay} />
        </>
      )}
      {tab === 'shifts' && <ShiftsPanel data={data} changed={changed} />}
      {tab === 'info' && <InfoPanel email={email ?? null} alerts={alerts} preview={!!preview} />}
      <StaffBottomBar tab={tab} onChange={setTab} todayCount={todayCount} alertsOff={alerts.read === 'ready' && alerts.state !== 'on'} />
    </div>
  );
}

type Load = { state: 'loading' } | { state: 'ready'; data: SelfViewData } | { state: 'error' };

// Composes the shared SWR trigger core with `revalidate: false` — deliberately
// TICK-LESS. This view mounts OUTSIDE the keep-alive shell (App.tsx branches by
// role before AppShell/PageHost, and the admin preview overlay is a fresh mount
// per open), so there is no activation tick to honour; and the only write on
// the staff branch is the sign-out POST, whose mutation-bus signal must NOT
// refetch the roster mid-sign-out.
function useSelfView(path: string): Load {
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const trigger = useFetchTrigger({ revalidate: false });
  useEffect(() => {
    const run = trigger.begin();
    api<SelfViewData>(path, run.init)
      .then((r) => {
        if (!run.isCurrent()) return;
        run.markContent();
        setLoad({ state: 'ready', data: r.data });
      })
      .catch((e) => {
        // A 401 redirects to login inside api(); anything else is a soft error.
        if (!(e instanceof ApiError && e.status === 401) && run.isCurrent()) setLoad({ state: 'error' });
      });
    return run.cancel;
  }, [path, trigger.nonce, trigger.tick]);
  return load;
}

// The Today briefing load — same tick-less pattern, with a manual retry for
// the in-card error state (both briefing cards share the one fetch).
// `expectedDate` is the date the payload SHOULD carry at a given instant, used
// only by the day-boundary staleness check — `dublinDateOf` (today) by
// default, `tomorrowExpected` / `dayAfterExpected` for the look-ahead loads.
// Callers pass a STABLE module-level function so the once-mounted interval
// closure stays correct.
function useTodayBriefing(path: string, expectedDate: (nowMs: number) => string = dublinDateOf): TodayLoad {
  const [load, setLoad] = useState<TodayLoad>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [revalidation, setRevalidation] = useState(0);
  const trigger = useFetchTrigger({ revalidate: false });
  // Last-known briefing, held across silent revalidations: a refetch never
  // drops ready content to a skeleton, and a failed revalidation keeps the
  // last-known lists instead of flashing the error card.
  const dataRef = useRef<MeToday | null>(null);
  useEffect(() => {
    const run = trigger.begin();
    if (!dataRef.current) setLoad({ state: 'loading' });
    api<MeToday>(path, run.init)
      .then((r) => {
        if (!run.isCurrent()) return;
        run.markContent();
        dataRef.current = r.data;
        setLoad({ state: 'ready', data: r.data });
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) return;
        if (run.isCurrent() && !dataRef.current)
          setLoad({ state: 'error', retry: () => setAttempt((a) => a + 1) });
      });
    return run.cancel;
  }, [path, attempt, revalidation, trigger.nonce, trigger.tick]);

  // Staleness triggers, all funnelled through ONE silent revalidation path:
  // returning to the tab (phone woken in the morning), a BFCache restore, and
  // the Dublin day boundary passing while the page stays open (overnight
  // shifts) — checked each minute against the day this briefing is FOR.
  // `expectedDate` computes what date the payload should carry NOW: today's
  // Dublin date for the Today load, +1/+2 for the look-ahead loads. Comparing
  // against a fixed "today" would make a look-ahead load — always ahead —
  // refetch on every single tick.
  useEffect(() => {
    const revalidate = () => setRevalidation((v) => v + 1);
    const onVisible = () => { if (document.visibilityState === 'visible') revalidate(); };
    const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) revalidate(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    const dayCheck = window.setInterval(() => {
      if (dataRef.current && dataRef.current.date !== expectedDate(Date.now())) revalidate();
    }, 60_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
      window.clearInterval(dayCheck);
    };
  }, []);
  return load;
}

// Record one closed dwell segment, dropping the empties: a zero-duration
// segment carries no information and StrictMode's dev double-mount would
// otherwise write one per visit. The single gate for every segment source.
const emitDwell = (seg: { label: string; durationMs: number } | null) => {
  if (seg && seg.durationMs > 0) recordPageView(seg.label, seg.durationMs);
};

// The full-screen staff page (role === 'staff'). Brand header + sign out, then
// the shared body. Mobile-first; reuses the app's design tokens.
// The dates a tapped alert put in the URL (`?changed=d1,d2`), read once and
// stripped so a refresh is an ordinary open (shift-alerts-send AC11).
function readChangedParam(): string[] {
  if (typeof window === 'undefined') return [];
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('changed');
  if (!raw) return [];
  const dates = raw.split(',').filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
  params.delete('changed');
  const rest = params.toString();
  window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
  return dates;
}

export function StaffView({ email }: { email?: string | null }) {
  const load = useSelfView('/me/shifts');
  const [changed] = useState<string[]>(readChangedParam);
  // The service worker behind shift alerts registers on the staff page only —
  // the admin app never needs it, and the preview must not (shift-alerts-info-tab AC13).
  useEffect(() => {
    registerWorker();
  }, []);
  const todayLoad = useTodayBriefing('/me/today');
  const tomorrowLoad = useTodayBriefing('/me/tomorrow', tomorrowExpected);
  const dayAfterLoad = useTodayBriefing('/me/day-after', dayAfterExpected);
  const [signingOut, setSigningOut] = useState(false);

  // Page-view dwell for the staff self-view (admin metrics), PER TAB — the
  // activity feed shows WHICH page the staff member is on (staff-today /
  // staff-shifts), not just that they signed in. Segments close on tab
  // switch; the open segment closes once on leave (pagehide or unmount).
  const dwellRef = useRef<ReturnType<typeof makeTabDwell> | null>(null);
  const currentTabRef = useRef<StaffTab>(changed.length > 0 ? 'shifts' : 'today');
  useEffect(() => {
    dwellRef.current = makeTabDwell(currentTabRef.current, Date.now);
    // pagehide flushes the open segment (tab close, navigation). A BFCache
    // restore brings the page back WITHOUT a remount, so pageshow(persisted)
    // arms a fresh tracker on the tab the user is still looking at —
    // otherwise the restored visit would record nothing.
    const flush = () => emitDwell(dwellRef.current?.flush() ?? null);
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) dwellRef.current = makeTabDwell(currentTabRef.current, Date.now);
    };
    window.addEventListener('pagehide', flush);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('pageshow', onPageShow);
      flush();
    };
  }, []);
  const onTabChange = (tab: StaffTab) => {
    currentTabRef.current = tab;
    emitDwell(dwellRef.current?.switch(tab) ?? null);
  };
  const signOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } catch {
      /* drop to sign-in regardless */
    }
    window.location.assign('/');
  };

  return (
    <main className="staff-page">
      <header className="staff-topbar">
        <div className="staff-topbar-inner">
          <div className="staff-brand">
            <BrandMark size={26} />
            <span>Beeches Brook</span>
          </div>
          <button className="staff-signout" onClick={signOut} disabled={signingOut}>
            <Icon name="log-out" size={14} />
            <span>{signingOut ? 'Signing out…' : 'Sign out'}</span>
          </button>
        </div>
      </header>

      {load.state === 'loading' && <div className="staff-note">Loading your shifts…</div>}
      {load.state === 'error' && <div className="staff-note">Couldn't load your shifts. Pull to refresh or try again later.</div>}
      {load.state === 'ready' && <StaffViewBody data={load.data} todayLoad={todayLoad} tomorrowLoad={tomorrowLoad} dayAfterLoad={dayAfterLoad} onTabChange={onTabChange} email={email ?? null} changed={changed} />}
    </main>
  );
}

// Admin preview (staff-self-view, admin preview). An admin/owner sees the
// staff member's rota via GET /api/staff/:id/shifts and their Today briefing
// via GET /api/staff/:id/today (both admin-gated, both sharing the staff
// view's own builders) — the preview IS the staff member's view, every tab.
// The helper's login comes with the id: the Info tab's "Signed in as" is
// theirs, not blank (review, PR #278).
export function StaffPreviewOverlay({ staffId, email, onClose }: { staffId: string; email: string | null; onClose: () => void }) {
  const load = useSelfView(`/staff/${staffId}/shifts`);
  const todayLoad = useTodayBriefing(`/staff/${staffId}/today`);
  const tomorrowLoad = useTodayBriefing(`/staff/${staffId}/tomorrow`, tomorrowExpected);
  const dayAfterLoad = useTodayBriefing(`/staff/${staffId}/day-after`, dayAfterExpected);
  return (
    <div className="staff-preview-backdrop" onClick={onClose}>
      <div className="staff-preview-frame" onClick={(e) => e.stopPropagation()}>
        <div className="staff-preview-banner">
          <Icon name="clock" size={14} />
          <span>Previewing the staff member's own view</span>
          <button className="staff-preview-close" onClick={onClose} aria-label="Close preview">
            <Icon name="close" size={16} />
          </button>
        </div>
        <main className="staff-page embedded">
          {load.state === 'loading' && <div className="staff-note">Loading…</div>}
          {load.state === 'error' && <div className="staff-note">Couldn't load this staff member's shifts.</div>}
          {load.state === 'ready' && <StaffViewBody data={load.data} todayLoad={todayLoad} tomorrowLoad={tomorrowLoad} dayAfterLoad={dayAfterLoad} email={email} preview={{ staffId }} />}
        </main>
      </div>
    </div>
  );
}
